import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/cloudinary';

export interface CarouselSlide {
  id: string;
  type?: 'gallery' | 'custom' | 'post';
  artwork_id?: string;
  post_slug?: string;
  image_url: string;
  title: string;
  subtitle: string;
  description: string;
  show_cta?: boolean;
  cta_label?: string;
  cta_url?: string;
}

interface FeaturedCarouselProps {
  items: CarouselSlide[];
}

export default function FeaturedCarousel({ items }: FeaturedCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [canAutoPlay, setCanAutoPlay] = useState(false);

  const slideCount = items.length;

  useEffect(() => {
    const timeout = setTimeout(() => setCanAutoPlay(true), 9000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isAutoPlay || !canAutoPlay) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slideCount);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlay, slideCount, canAutoPlay]);

  const goToPrevious = () => {
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + slideCount) % slideCount);
  };

  const goToNext = () => {
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % slideCount);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlay(false);
    setCurrentIndex(index);
  };

  const optimizedHeroImage = (url: string, width: number) => getOptimizedImageUrl(url, width, 1080);

  const imageSrcSet = (url: string) => [640, 960, 1280, 1600, 1920]
    .map((w) => `${optimizedHeroImage(url, w)} ${w}w`)
    .join(', ');

  const current = items[currentIndex];
  const currentCtaTo = current.show_cta === false
    ? null
    : current.type === 'gallery' && current.artwork_id
      ? `/gallery/${current.artwork_id}`
      : current.type === 'post' && current.post_slug
        ? `/blog/${current.post_slug}`
        : current.type === 'custom' && current.cta_url
          ? current.cta_url
          : null;
  const currentCtaLabel = current.cta_label?.trim()
    || (current.type === 'post' ? 'Read Article' : current.type === 'custom' ? 'Learn More' : 'View Artwork');
  const isExternalCta = !!currentCtaTo && /^https?:\/\//.test(currentCtaTo);

  return (
    <div className="relative w-full h-[100svh] md:h-screen bg-black overflow-hidden group">
      {/* Slides */}
      <div className="relative w-full h-full">
        {items.map((item, index) => {
          const isCurrent = index === currentIndex;
          const isInitialLcpCandidate = !canAutoPlay && index === 0;
          const isNearCurrent =
            index === currentIndex ||
            index === (currentIndex + 1) % slideCount ||
            index === (currentIndex - 1 + slideCount) % slideCount;

          return (
            <div
              key={item.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {isNearCurrent && (
                <img
                  src={optimizedHeroImage(item.image_url, 1280)}
                  srcSet={imageSrcSet(item.image_url)}
                  sizes="100vw"
                  alt={item.title}
                  width={1920}
                  height={1080}
                  loading={isCurrent || isInitialLcpCandidate ? 'eager' : 'lazy'}
                  fetchPriority={isCurrent || isInitialLcpCandidate ? 'high' : 'auto'}
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
            </div>
          );
        })}
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 flex items-end">
        <div className="w-full p-8 pb-24 md:p-16 text-white">
          <div className="max-w-2xl space-y-4">
            <div className="space-y-2">
              <h1 className="text-4xl md:text-6xl font-light tracking-tight">
                {current.title}
              </h1>
            </div>
            <p className="text-lg md:text-xl text-gray-200 font-light">
              {current.subtitle}
            </p>
            <p className="text-base md:text-lg text-gray-300 max-w-xl leading-relaxed mt-4">
              {current.description}
            </p>
            {currentCtaTo && (
              isExternalCta ? (
                <a
                  href={currentCtaTo}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex mt-6 px-8 py-3 bg-white text-black font-semibold hover:bg-gray-200 transition-colors"
                >
                  {currentCtaLabel}
                </a>
              ) : (
                <Link
                  to={currentCtaTo}
                  className="inline-flex mt-6 px-8 py-3 bg-white text-black font-semibold hover:bg-gray-200 transition-colors"
                >
                  {currentCtaLabel}
                </Link>
              )
            )}
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        onMouseEnter={() => setIsAutoPlay(false)}
        onMouseLeave={() => setIsAutoPlay(true)}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 p-3 min-w-11 min-h-11 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all duration-300 opacity-0 group-hover:opacity-100"
        aria-label="Previous slide"
      >
        <ChevronLeft size={32} />
      </button>

      <button
        onClick={goToNext}
        onMouseEnter={() => setIsAutoPlay(false)}
        onMouseLeave={() => setIsAutoPlay(true)}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 p-3 min-w-11 min-h-11 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all duration-300 opacity-0 group-hover:opacity-100"
        aria-label="Next slide"
      >
        <ChevronRight size={32} />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`min-w-11 min-h-11 inline-flex items-center justify-center transition-all duration-300 ${
              index === currentIndex ? 'text-white' : 'text-white/70 hover:text-white'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          >
            <span
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-current w-8' : 'bg-current w-2'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Counter */}
      <div className="absolute top-8 right-8 text-white text-sm md:text-base font-light tracking-widest">
        <span className="font-semibold">{(currentIndex + 1).toString().padStart(2, '0')}</span>
        <span className="text-gray-400"> / {slideCount.toString().padStart(2, '0')}</span>
      </div>
    </div>
  );
}